/**
 * Production-safe logger utility
 * Reduces console noise in production while keeping debug capability
 */

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

class Logger {
  private level: LogLevel;
  private isProduction: boolean;

  constructor() {
    this.isProduction = import.meta.env.PROD;
    this.level = this.getLogLevel();
  }

  private getLogLevel(): LogLevel {
    // Allow debug override in production
    const urlParams = new URLSearchParams(window.location.search);
    const urlLevel = urlParams.get('log');
    const storageLevel = localStorage.getItem('LOG_LEVEL');
    
    if (urlLevel && ['debug', 'info', 'warn', 'error'].includes(urlLevel)) {
      return urlLevel as LogLevel;
    }
    
    if (storageLevel && ['debug', 'info', 'warn', 'error'].includes(storageLevel)) {
      return storageLevel as LogLevel;
    }

    // Default levels - info in dev, warn in production
    return this.isProduction ? 'warn' : 'info';
  }

  private shouldLog(level: LogLevel): boolean {
    const levels = { debug: 0, info: 1, warn: 2, error: 3 };
    return levels[level] >= levels[this.level];
  }

  debug(...args: any[]) {
    if (this.shouldLog('debug')) {
      console.debug(...args);
    }
  }

  info(...args: any[]) {
    if (this.shouldLog('info')) {
      console.info(...args);
    }
  }

  warn(...args: any[]) {
    if (this.shouldLog('warn')) {
      console.warn(...args);
    }
  }

  error(...args: any[]) {
    if (this.shouldLog('error')) {
      console.error(...args);
    }
  }

  // Special method for critical errors that should always show
  critical(...args: any[]) {
    console.error('[CRITICAL]', ...args);
  }
}

export const logger = new Logger();

// Helper to enable debug logging in production
export const enableDebugLogging = () => {
  localStorage.setItem('LOG_LEVEL', 'debug');
  window.location.reload();
};

export const disableDebugLogging = () => {
  localStorage.removeItem('LOG_LEVEL');
  window.location.reload();
};